/*
 *   File : add-nominee-card.js
 *   Author : https://evoqins.com
 *   Description : Nominee details card component
 *   Integrations : NA
 *   Version : 1.0.0
*/


import { useEffect, useState, memo } from 'react';
import { differenceInYears, subDays, format } from 'date-fns';
import { Modal } from 'bootstrap';

//custom component
import { CustomTextInput, DatePicker } from '../Form-Elements';
import { SelectionSuggestModal } from '../Modal';
import { _formatDateFormat } from '../../Helper';

const EMPTY_ADDRESS = {
    "address_line1": null,
    "address_line2": null,
    "state_id": null,
    "state_name": null,
    "city_id": null,
    "city": null,
    "pincode": null,
    "country_id": null,
    "country": null
};

const EMPTY_CONTACT = {
    "email": null,
    "country_code": null,
    "phone_number": null
};

const AddNomineeCard = (props) => {

    const [name, setName] = useState("");
    const [nameError, setNameError] = useState("");

    const [selectedRelation, setSelectedRelation] = useState([]);
    const [relationError, setRelationError] = useState("");

    const [allocation, setAllocation] = useState("");
    const [allocationError, setAllocationError] = useState("");

    const [dob, setDob] = useState("");
    const [dobError, setDobError] = useState("");

    const [relationData, setRelationData] = useState(props.data);

    const [guardianName, setGuardianName] = useState("");
    const [guardianNameError, setGuardianNameError] = useState("");

    const [selectedGuardianRelation, setSelectedGuardianRelation] = useState([]);
    const [guardianRelationError, setGuardianRelationError] = useState("");

    const [nomineeId, setNomineeId] = useState(null);
    const [initialStatus, setInitialStatus] = useState(true);
    const [showRelationModal, setShowRelationModal] = useState(false);
    const [showGuardianRelationModal, setShowGuardianRelationModal] = useState(false);

    const IS_MINOR = dob ? differenceInYears(new Date(), new Date(dob)) < 18 : false;

    useEffect(() => {
        if (props.nomineeData?.nominee_id !== null) {
            setNomineeId(props.nomineeData.nominee_id);
        }
        if (props.nomineeData?.name !== null) {
            setName(props.nomineeData.name);
        }
        if (props.nomineeData.dob !== null) {
            setDob(_formatDateFormat(props.nomineeData.dob));
        }

        if (props.nomineeData?.allocation !== null && props.nomineeData?.allocation !== undefined) {
            setAllocation(props.nomineeData.allocation);
        } else if (!props.showAllocation) {
            setAllocation(100);
        }

        if (props.nomineeData?.guardian_name !== null) {
            setGuardianName(props.nomineeData.guardian_name);
        }

        if (props.nomineeData?.relation_id !== null) {
            let selected_relation = props.data?.kyc_relationship?.find((data) => data.id == props.nomineeData.relation_id);
            setSelectedRelation(selected_relation);
        } else {
            setSelectedRelation(props.data?.kyc_relationship?.find(data => data.name === "Spouse"));
        }

        if (props.data && props.data.nominee_minor_relation && props.nomineeData?.guardian_relationship_id !== null) {
            let selected_guardian = props.data?.nominee_minor_relation?.find((data) => data.id == props.nomineeData.guardian_relationship_id);
            setSelectedGuardianRelation(selected_guardian);
        }

        setInitialStatus(false);
    }, [props.nomineeData]);

    useEffect(() => {
        if (name === "") {
            setNameError(props.error?.name_error);
        }

        if (!selectedRelation?.id) {
            setRelationError(props.error?.relation_id_error);
        }

        if (props.showAllocation && allocation === "") {
            setAllocationError(props.error?.allocation_error);
        }

        if (dob === "") {
            setDobError(props.error?.dob_error);
        }

        if (IS_MINOR) {
            if (guardianName === "") {
                setGuardianNameError(props.error?.guardian_name_error);
            }

            if (!selectedGuardianRelation?.id) {
                setGuardianRelationError(props.error?.guardian_relationship_id_error);
            }
        }
    }, [props.error, IS_MINOR]);

    useEffect(() => {
        if (props.fetchNominee === true) {
            const nominee = {
                "id": props.nomineeData?.id,
                "nominee_id": nomineeId || null,
                "name": name.length !== 0 ? name : null,
                "relation_id": selectedRelation?.id || null,
                "relation": selectedRelation?.name || null,
                "dob": dob ? format(new Date(dob), "dd-MM-yyyy") : null,
                "allocation": props.showAllocation
                    ? (allocation !== "" && allocation !== null ? Number(allocation) : null)
                    : 100,
                "guardian_name": IS_MINOR && guardianName?.length !== 0 ? guardianName : null,
                "guardian_relationship_id": IS_MINOR && selectedGuardianRelation?.id ? selectedGuardianRelation.id : null,
                "guardian_relationship": IS_MINOR && selectedGuardianRelation?.name ? selectedGuardianRelation.name : null,
            };

            props.getNomineeData(nominee, props.index);
        }
    }, [props.fetchNominee]);

    useEffect(() => {
        if (showRelationModal === true) {
            let relation_modal = new Modal(document.getElementById(`select-suggest-modal${props.index}`));
            relation_modal.show();
        }

        if (showGuardianRelationModal) {
            let guardian_relation = new Modal(document.getElementById(`select-guardian-relation${props.index}`));
            guardian_relation.show();
        }
    }, [showRelationModal, showGuardianRelationModal]);

    useEffect(() => {
        if (props.data) {
            setRelationData(props.data);
        }
    }, [props.data]);

    useEffect(() => {
        if (!props.showAllocation) {
            setAllocation(100);
            setAllocationError("");
        }
    }, [props.showAllocation]);

    const _handleNameChange = (value) => {
        setName(value);
        setNameError("");
    };

    const _handleSelectRelation = (value) => {
        setSelectedRelation(value);
        setRelationError("");
    };

    const _handleAllocationChange = (value) => {
        let allocation_value = value ? Number(value) : null;
        if (allocation_value <= 100) {
            setAllocation(value);
            setAllocationError("");
        }
    };

    const _handleDateChange = (date) => {
        setDob(date);
        setDobError("");
    };

    const _handleGuardianNameChange = (value) => {
        setGuardianName(value);
        setGuardianNameError("");
    };

    const _handleShowRelationModal = () => {
        setShowRelationModal(true);
    };

    const _handleShowGuardianRelationModal = () => {
        setShowGuardianRelationModal(true);
    };

    const _handleSelectGuardianRelation = (value) => {
        setSelectedGuardianRelation(value);
        setGuardianRelationError("");
    };

    return (
        <>
            {
                initialStatus === false &&
                <>
                    <div className="row gx-gy-32px">
                        <div className="col-sm-6 col-lg-4">
                            <div id={`name-${props.index}`}>
                                <CustomTextInput label="Nominee name*"
                                    placeholder="Joseph Thomas"
                                    type="text"
                                    floatingLabel
                                    value={name}
                                    errorMsg={nameError}
                                    handleChange={_handleNameChange} />
                            </div>
                        </div>

                        <div className="col-sm-6 col-lg-4">
                            <div id={`relation-${props.index}`}
                                className='position-relative'>
                                <CustomTextInput label="Relationship*"
                                    placeholder="Select relation"
                                    dropdown={true}
                                    type="text"
                                    floatingLabel
                                    isOpen={showRelationModal}
                                    value={selectedRelation.display_name}
                                    errorMsg={relationError}
                                    readOnly
                                    onClick={_handleShowRelationModal} />
                            </div>
                        </div>

                        <div className="col-sm-6 col-lg-4">
                            <div id={`dob-${props.index}`}>
                                <DatePicker value={dob}
                                    label="Date of birth*"
                                    error={dobError}
                                    floatingLabel
                                    activeDate={subDays(new Date(), 1)}
                                    maxDate={subDays(new Date(), 1)}
                                    onChange={_handleDateChange} />
                            </div>
                        </div>

                        {
                            props.showAllocation &&
                            <div className="col-sm-6 col-lg-4">
                                <div id={`allocation-${props.index}`}>
                                    <CustomTextInput label="Allocation*"
                                        placeholder="Eg:50%"
                                        type="number"
                                        floatingLabel
                                        maxLength={3}
                                        maxValue={100}
                                        value={allocation}
                                        errorMsg={allocationError}
                                        handleChange={_handleAllocationChange} />
                                </div>
                            </div>
                        }
                    </div>

                    {
                        IS_MINOR &&
                        <div>
                            <div className="col-12">
                                <p className="e-text-onyx e-font-12-res e-font-14 e-alt-font-poppins e-font-weight-400 line-height-24px e-border-radius-16 p-2 p-sm-3 e-bg-cosmic-latte my-3">
                                    Since the nominee is a minor (under 18 years), please provide the details of a guardian to complete the nomination process.
                                </p>
                            </div>

                            <div className='row gx-gy-32px'>
                                <div className="col-sm-6 col-lg-4">
                                    <div id={`guardian-name-${props.index}`}>
                                        <CustomTextInput label="Guardian name*"
                                            placeholder="John Doe"
                                            type="text"
                                            floatingLabel
                                            value={guardianName}
                                            errorMsg={guardianNameError}
                                            handleChange={_handleGuardianNameChange} />
                                    </div>
                                </div>

                                <div className="col-sm-6 col-lg-4">
                                    <div id={`guardian-relation-${props.index}`}>
                                        <CustomTextInput label="Relation*"
                                            placeholder="Select relation"
                                            dropdown={true}
                                            type="text"
                                            floatingLabel
                                            isOpen={showGuardianRelationModal}
                                            value={selectedGuardianRelation.name}
                                            errorMsg={guardianRelationError}
                                            readOnly
                                            className="cursor-pointer"
                                            onClick={_handleShowGuardianRelationModal} />
                                    </div>
                                </div>
                            </div>
                        </div>
                    }

                    {
                        showRelationModal &&
                        <SelectionSuggestModal title="Choose a relation*"
                            data={relationData.kyc_relationship}
                            type={props.index}
                            id={`select-suggest-modal${props.index}`}
                            selectedOption={selectedRelation}
                            onSelectOption={_handleSelectRelation}
                            onModalClose={() => setShowRelationModal(false)} />
                    }

                    {
                        showGuardianRelationModal &&
                        <SelectionSuggestModal title="Choose a guardian relation*"
                            data={relationData.nominee_minor_relation}
                            type={2}
                            id={`select-guardian-relation${props.index}`}
                            selectedOption={selectedGuardianRelation}
                            onSelectOption={_handleSelectGuardianRelation}
                            onModalClose={() => setShowGuardianRelationModal(false)} />
                    }
                </>
            }
        </>
    )
}

export default memo(AddNomineeCard);

AddNomineeCard.defaultProps = {
    onChange: () => { },
    type: 1
}
